import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { CheckCircleIcon, ArrowRightIcon } from "@heroicons/react/solid";
import Button from "../../components/common/Button";
import { completeInitialSetupThunk } from "../../store/thunks/authThunks";

// Import step components
import ChangePasswordStep from "./components/ChangePasswordStep";
import CompanyDetailsStep from "./components/CompanyDetailsStep";
import VerifyRatesStep from "./components/VerifyRatesStep";
import CreateBranchStep from "./components/CreateBranchStep";
import CreateAdminStep from "./components/CreateAdminStep";

const InitialSetup = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, error, token, setupSteps, user } = useSelector(
    (state) => state.auth
  );

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [setupData, setSetupData] = useState({});
  const [showCompletionMessage, setShowCompletionMessage] = useState(false);

  useEffect(() => {
    if (token && !error) {
      // Check if any required setup steps are incomplete
      const hasIncompleteRequiredSteps = setupSteps?.some(
        (step) => step.required && !step.completed
      );

      if (!hasIncompleteRequiredSteps) {
        navigate("/");
      }
    }
  }, [token, navigate, setupSteps, error]);

  useEffect(() => {
    if (setupSteps && setupSteps.length > 0) {
      // Find first incomplete required step
      const firstIncompleteIndex = setupSteps.findIndex(
        (step) => step.required && !step.completed
      );
      if (firstIncompleteIndex !== -1) {
        setCurrentStepIndex(firstIncompleteIndex);
      }

      // Track completed steps
      const completed = setupSteps
        .filter((step) => step.completed)
        .map((step) => step.step);
      setCompletedSteps(completed);
    }
  }, [setupSteps]);

  useEffect(() => {
    // Show completion message when all required steps are done
    if (isAllRequiredStepsComplete()) {
      setShowCompletionMessage(true);
      setTimeout(() => setShowCompletionMessage(false), 5000);
    }
  }, [completedSteps]);

  const currentStep = setupSteps?.[currentStepIndex];

  const handleStepComplete = (stepName, data) => {
    setCompletedSteps([...completedSteps, stepName]);
    setSetupData({ ...setupData, [stepName]: data });

    // Move to next step
    if (currentStepIndex < setupSteps.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
    }
  };

  const handleSkipStep = () => {
    if (currentStep && !currentStep.required) {
      if (currentStepIndex < setupSteps.length - 1) {
        setCurrentStepIndex(currentStepIndex + 1);
      }
    }
  };

  const handlePreviousStep = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
    }
  };

  const handleCompleteSetup = async () => {
    try {
      //   await dispatch(completeInitialSetupThunk(setupData)).unwrap();
      navigate("/");
    } catch (error) {
      console.error("Failed to complete setup:", error);
    }
  };

  const isAllRequiredStepsComplete = () => {
    return setupSteps
      ?.filter((step) => step.required)
      .every((step) => completedSteps.includes(step.step));
  };

  const getRequiredStepsProgress = () => {
    const requiredSteps = setupSteps?.filter((step) => step.required) || [];
    const completedRequired = requiredSteps.filter((step) =>
      completedSteps.includes(step.step)
    );
    return {
      completed: completedRequired.length,
      total: requiredSteps.length,
      percentage:
        requiredSteps.length > 0
          ? (completedRequired.length / requiredSteps.length) * 100
          : 0,
    };
  };

  const renderStepComponent = () => {
    if (!currentStep) return null;

    switch (currentStep.step) {
      case "CHANGE_PASSWORD":
        return (
          <ChangePasswordStep
            onComplete={(data) => handleStepComplete("CHANGE_PASSWORD", data)}
          />
        );
      case "COMPANY_DETAILS":
        return (
          <CompanyDetailsStep
            onComplete={(data) => handleStepComplete("COMPANY_DETAILS", data)}
          />
        );
      case "VERIFY_RATES":
        return (
          <VerifyRatesStep
            onComplete={(data) => handleStepComplete("VERIFY_RATES", data)}
          />
        );
      case "CREATE_BRANCH":
        return (
          <CreateBranchStep
            onComplete={(data) => handleStepComplete("CREATE_BRANCH", data)}
          />
        );
      case "CREATE_ADMIN":
        return (
          <CreateAdminStep
            onComplete={(data) => handleStepComplete("CREATE_ADMIN", data)}
          />
        );
      default:
        return null;
    }
  };

  if (!setupSteps || setupSteps.length === 0) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading setup...</p>
        </div>
      </div>
    );
  }

  const requiredProgress = getRequiredStepsProgress();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Initial System Setup
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Welcome {user?.name}! Let's get your system ready.
              </p>
            </div>

            <div className="flex items-center space-x-4">
              {/* Required Steps Progress */}
              <div className="text-right">
                <p className="text-xs text-gray-500">Required Steps</p>
                <p className="text-sm font-medium text-gray-900">
                  {requiredProgress.completed} of {requiredProgress.total}{" "}
                  complete
                </p>
              </div>

              {/* Go to Dashboard Button */}
              {isAllRequiredStepsComplete() && (
                <Button
                  variant="success"
                  onClick={handleCompleteSetup}
                  icon={<ArrowRightIcon className="w-4 h-4" />}
                  size="md"
                  className="animate-pulse"
                >
                  Go to Dashboard
                </Button>
              )}
            </div>
          </div>

          {/* Completion Message Banner */}
          {showCompletionMessage && isAllRequiredStepsComplete() && (
            <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center">
                <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-green-800">
                    All required steps completed!
                  </p>
                  <p className="text-xs text-green-600 mt-1">
                    You can now proceed to the dashboard or complete optional
                    steps for a better experience.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="lg:grid lg:grid-cols-12 lg:gap-8">
          {/* Sidebar - Progress Steps */}
          <div className="lg:col-span-3 mb-8 lg:mb-0">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">
                Setup Progress
              </h3>
              <nav className="space-y-2">
                {setupSteps.map((step, index) => {
                  const isActive = index === currentStepIndex;
                  const isCompleted = completedSteps.includes(step.step);

                  return (
                    <button
                      key={step.step}
                      onClick={() => setCurrentStepIndex(index)}
                      className={`
                        w-full text-left px-3 py-2 rounded-lg transition-colors
                        ${
                          isActive
                            ? "bg-primary text-white"
                            : isCompleted
                            ? "bg-green-50 text-green-700 hover:bg-green-100"
                            : "bg-gray-50 text-gray-700 hover:bg-gray-100"
                        }
                      `}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <span
                            className={`
                            inline-flex items-center justify-center w-6 h-6 rounded-full text-xs mr-3
                            ${
                              isActive
                                ? "bg-white text-primary"
                                : isCompleted
                                ? "bg-green-600 text-white"
                                : "bg-gray-300 text-gray-600"
                            }
                          `}
                          >
                            {isCompleted ? (
                              <CheckCircleIcon className="w-4 h-4" />
                            ) : (
                              index + 1
                            )}
                          </span>
                          <div>
                            <p
                              className={`text-sm font-medium ${
                                isActive
                                  ? ""
                                  : isCompleted
                                  ? ""
                                  : "text-gray-600"
                              }`}
                            >
                              {step.description}
                            </p>
                            {step.required && (
                              <span
                                className={`text-xs ${
                                  isActive ? "text-white/80" : "text-gray-500"
                                }`}
                              >
                                Required
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </nav>

              {/* Overall Progress */}
              <div className="mt-6 pt-6 border-t">
                <div className="flex justify-between text-sm text-gray-600 mb-2">
                  <span>Overall Progress</span>
                  <span>
                    {completedSteps.length}/{setupSteps.length}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-primary h-2 rounded-full transition-all duration-300"
                    style={{
                      width: `${
                        (completedSteps.length / setupSteps.length) * 100
                      }%`,
                    }}
                  />
                </div>

                {/* Required Steps Progress */}
                <div className="mt-4">
                  <div className="flex justify-between text-sm text-gray-600 mb-2">
                    <span>Required Steps</span>
                    <span className="font-medium">
                      {requiredProgress.completed}/{requiredProgress.total}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-green-600 h-2 rounded-full transition-all duration-300"
                      style={{
                        width: `${requiredProgress.percentage}%`,
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Quick Complete Button in Sidebar */}
              {isAllRequiredStepsComplete() && (
                <div className="mt-6 pt-6 border-t">
                  <Button
                    variant="primary"
                    onClick={handleCompleteSetup}
                    className="w-full"
                    size="sm"
                  >
                    Finish Setup
                  </Button>
                  <p className="text-xs text-gray-500 mt-2 text-center">
                    All required steps are complete
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-9">
            <div className="bg-white rounded-lg shadow-sm">
              {/* Step Header */}
              <div className="border-b px-6 py-4">
                <h2 className="text-xl font-semibold text-gray-900">
                  {currentStep?.description}
                </h2>
                {currentStep?.required && (
                  <p className="text-sm text-amber-600 mt-1">
                    This step is required and cannot be skipped
                  </p>
                )}
                {!currentStep?.required && isAllRequiredStepsComplete() && (
                  <p className="text-sm text-green-600 mt-1">
                    This step is optional. You can skip it and go to dashboard.
                  </p>
                )}
              </div>

              {/* Step Content */}
              <div className="p-6">{renderStepComponent()}</div>

              {/* Navigation */}
              <div className="border-t px-6 py-4 bg-gray-50 rounded-b-lg">
                <div className="flex items-center justify-between">
                  <Button
                    variant="secondary-outline"
                    onClick={handlePreviousStep}
                    disabled={currentStepIndex === 0}
                  >
                    Previous
                  </Button>

                  <div className="flex items-center space-x-3">
                    {currentStep && !currentStep.required && (
                      <Button
                        variant="secondary-outline"
                        onClick={handleSkipStep}
                      >
                        Skip for Now
                      </Button>
                    )}

                    {currentStepIndex === setupSteps.length - 1 &&
                    isAllRequiredStepsComplete() ? (
                      <Button variant="success" onClick={handleCompleteSetup}>
                        Complete Setup
                      </Button>
                    ) : isAllRequiredStepsComplete() ? (
                      <Button
                        variant="primary-outline"
                        onClick={handleCompleteSetup}
                      >
                        Go to Dashboard →
                      </Button>
                    ) : (
                      <div className="text-sm text-gray-500">
                        Complete this step to continue
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InitialSetup;
